import React,{useState} from 'react';
import {motion} from 'framer-motion';
import {useInView} from 'react-intersection-observer';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiZap,FiCode,FiBarChart3,FiLayers,FiTarget,FiClock,FiTrendingUp,FiMessageSquare,FiFileText,FiUsers,FiArrowRight,FiStar,FiCalendar,FiPlay,FiSettings,FiDatabase,FiFilter,FiCpu,FiShield}=FiIcons;

const Solutions=()=> {
  const [headerRef,headerInView]=useInView({triggerOnce: true,threshold: 0.1});
  const [tabsRef,tabsInView]=useInView({triggerOnce: true,threshold: 0.1});
  const [activeTab,setActiveTab]=useState('ai-studio');
  const [selectedCategory,setSelectedCategory]=useState('all');

  const tabs=[
    {id: 'ai-studio',name: 'AI Studio Projects',icon: FiCpu,description: 'Google AI Studio to MVP'},
    {id: 'use-cases',name: 'Use Cases',icon: FiTarget,description: 'Real-world applications'},
    {id: 'case-studies',name: 'Case Studies',icon: FiBarChart3,description: 'Proven implementations'}
  ];

  // Use Cases Data
  const useCases=[
    {
      icon: FiMessageSquare,
      title: 'AI Support Assistant (LLM + Tools)',
      problem: 'Support volumes rising;need faster,consistent responses.',
      solution: 'Domain-aware assistant with retrieval,FAQs,ticket integration.',
      outcome: 'Deflect tier-1 tickets;improve first-response speed.',
      buildBasis: 'ConversaAI + Aegis (guardrails) + LLM evaluation harness.',
      metrics: ['50% reduction in response time','30% ticket deflection','90% accuracy rate'],
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: FiBarChart3,
      title: 'Compliance & Ops Dashboard',
      problem: 'KPIs,audits,CAPA scattered across teams.',
      solution: 'Next.js + Supabase dashboard with workflows and alerts.',
      outcome: 'Operations visibility,audit readiness,reduced process gaps.',
      buildBasis: 'quality-compliance-dashboard repo.',
      metrics: ['100% audit readiness','40% faster CAPA resolution','Real-time KPI tracking'],
      gradient: 'from-blue-500 to-indigo-500'
    },
    {
      icon: FiUsers,
      title: 'Sales Enablement Bot',
      problem: 'Reps need fast collateral generation and query answers.',
      solution: 'LLM assistant with product docs,pricing logic,CRM snippets.',
      outcome: 'Faster proposals,consistent messaging.',
      buildBasis: 'Custom RAG implementation with sales data integration.',
      metrics: ['60% faster proposal creation','25% increase in win rate','Consistent messaging'],
      gradient: 'from-green-500 to-teal-500'
    },
    {
      icon: FiFileText,
      title: 'Content to Slides Automation',
      problem: 'Teams burn hours turning outlines into slides.',
      solution: 'SmartSlidesAI to convert structured inputs into branded decks.',
      outcome: 'Minutes to first draft,consistent visual standards.',
      buildBasis: 'SmartSlidesAI repository.',
      metrics: ['90% time savings','Brand consistency','Template standardization'],
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: FiCode,
      title: 'Developer Productivity (Local LLM Bridges)',
      problem: 'Cost/privacy for hosted LLMs in dev tooling.',
      solution: 'LLMBridgeClaudeCode maps familiar clients to local models via Ollama.',
      outcome: 'Lower cost,privacy,rapid iteration.',
      buildBasis: 'LLMBridgeClaudeCode repository.',
      metrics: ['70% cost reduction','Full privacy control','Offline capability'],
      gradient: 'from-cyan-500 to-blue-500'
    }
  ];

  // Case Studies Data
  const featuredCaseStudies=[
    {
      icon: FiFileText,
      title: 'SmartSlidesAI',
      subtitle: 'Internal/Open-Source Lineage',
      context: 'Internal productization using Next.js and LLM prompting for outline and formatting.',
      approach: [
        'Structured content schema → AI outline → templating system → export',
        'Guardrails for brand consistency;evaluation for quality/latency'
      ],
      results: [
        'Draft slide creation time reduced from hours to minutes in internal tests',
        '90% time savings on presentation creation',
        'Consistent brand compliance across all outputs'
      ],
      artifacts: 'SmartSlidesAI repository (internal)',
      metrics: {timeSaved: '90%',efficiency: '5x faster',accuracy: '95%'},
      gradient: 'from-green-500 to-teal-500'
    },
    {
      icon: FiBarChart3,
      title: 'Compliance Dashboard',
      subtitle: 'Integration & Automation',
      context: 'Need for centralized KPIs,audit readiness,and CAPA tracking.',
      approach: [
        'Next.js + Supabase;roles/permissions;audit workflow',
        'Optional AI assistant to summarize findings and suggest actions'
      ],
      results: [
        'Internal trials showed clearer ownership and faster CAPA turnaround',
        '100% audit readiness achieved',
        '40% reduction in compliance issue resolution time'
      ],
      artifacts: 'quality-compliance-dashboard repository (internal)',
      metrics: {auditReady: '100%',resolution: '40% faster',visibility: 'Real-time'},
      gradient: 'from-blue-500 to-indigo-500'
    },
    {
      icon: FiCode,
      title: 'LLMBridgeClaudeCode',
      subtitle: 'DevEx & Cost Control',
      context: 'Desire to prototype with local models while keeping developer ergonomics.',
      approach: [
        'Python proxy to let Anthropic-style clients work with local models (Ollama)',
        'Seamless integration with existing development workflows'
      ],
      results: [
        'Lowered experimentation costs;enabled offline privacy-preserving workflows',
        '70% reduction in LLM API costs',
        'Full privacy control for sensitive development'
      ],
      artifacts: 'LLMBridgeClaudeCode repository (internal)',
      metrics: {costSaving: '70%',privacy: '100%',offline: 'Full support'},
      gradient: 'from-purple-500 to-pink-500'
    }
  ];

  // AI Studio Projects Data
  const portfolioCategories=[
    {id: 'all',name: 'All Projects',icon: FiLayers,count: 35},
    {id: 'ai-platforms',name: 'AI Platforms',icon: FiZap,count: 7},
    {id: 'web-apps',name: 'Web & Apps',icon: FiCode,count: 8},
    {id: 'data-analytics',name: 'Data & Analytics',icon: FiBarChart3,count: 6},
    {id: 'tools',name: 'Tools & Utilities',icon: FiSettings,count: 7}
  ];

  const vibecodingMVPs=[
    // AI Platforms & Automation
    {
      category: 'ai-platforms',
      title: 'GenResAI Family',
      description: 'AI-powered career management and resume optimization suite',
      metrics: 'Career optimization',
      tags: ['AI','Career','Optimization'],
      gradient: 'from-purple-500 to-blue-500'
    },
    {
      category: 'ai-platforms',
      title: 'CORPsAI SOP Generator',
      description: 'Automated standard operating procedures with industry best practices',
      metrics: 'Process automation',
      tags: ['SOP','Automation','Enterprise'],
      gradient: 'from-green-500 to-teal-500'
    },
    {
      category: 'ai-platforms',
      title: 'ConversaAI',
      description: 'Specialized AI assistant for custom workflow automation',
      metrics: 'Custom workflows',
      tags: ['AI Assistant','Workflows','Custom'],
      gradient: 'from-blue-500 to-indigo-500'
    },
    {
      category: 'ai-platforms',
      title: 'Greata AI Bot',
      description: 'Multi-provider AI chat assistant with unified interface',
      metrics: 'Multi-provider support',
      tags: ['Chatbot','Multi-provider','AI'],
      gradient: 'from-cyan-500 to-blue-500'
    },
    {
      category: 'ai-platforms',
      title: 'AI Insight Hub Weekly Digest',
      description: 'Curated AI news and insights powered by Gemini API',
      metrics: 'Weekly AI insights',
      tags: ['News','Gemini API','Curation'],
      gradient: 'from-orange-500 to-red-500'
    },
    {
      category: 'ai-platforms',
      title: 'Unified AI Multimodal Workspace',
      description: 'Collaborative AI workspace for multimodal interactions',
      metrics: 'Multimodal AI',
      tags: ['Multimodal','Workspace','Collaboration'],
      gradient: 'from-pink-500 to-purple-500'
    },
    // Web & App Experiences
    {
      category: 'web-apps',
      title: 'VibeCoding AI Agency Website',
      description: 'Modern agency website showcasing AI development capabilities',
      metrics: 'Agency showcase',
      tags: ['Agency','Website','Modern'],
      gradient: 'from-indigo-500 to-purple-500'
    },
    {
      category: 'web-apps',
      title: 'ShowGitHubRepo Visual',
      description: 'Instant repository visualization and documentation tool',
      metrics: 'Repo visualization',
      tags: ['GitHub','Visualization','Documentation'],
      gradient: 'from-gray-600 to-gray-800'
    },
    {
      category: 'web-apps',
      title: 'BMAD Visual Platform',
      description: 'AI-powered agent development and visualization platform',
      metrics: 'Agent development',
      tags: ['Agents','Visual','Development'],
      gradient: 'from-teal-500 to-green-500'
    },
    {
      category: 'web-apps',
      title: 'ChatGuy',
      description: 'Modern chat interface with enhanced user experience',
      metrics: 'Chat interface',
      tags: ['Chat','UI','Modern'],
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      category: 'web-apps',
      title: 'Guy.dev Portfolio',
      description: 'Clean,professional developer portfolio and landing page',
      metrics: 'Developer showcase',
      tags: ['Portfolio','Developer','Clean'],
      gradient: 'from-green-500 to-blue-500'
    },
    {
      category: 'web-apps',
      title: 'Next.js Blog Theme',
      description: 'Production-ready blog template with modern design',
      metrics: 'Blog template',
      tags: ['Blog','Next.js','Template'],
      gradient: 'from-purple-500 to-pink-500'
    },
    // Data,Analytics & Automation
    {
      category: 'data-analytics',
      title: 'YouTube Creator Analytics Platform',
      description: 'Comprehensive analytics dashboard for content creators',
      metrics: 'Creator analytics',
      tags: ['YouTube','Analytics','Creators'],
      gradient: 'from-red-500 to-pink-500'
    },
    {
      category: 'data-analytics',
      title: 'Inventory Management Suite',
      description: 'Real-time inventory tracking and management system',
      metrics: 'Real-time tracking',
      tags: ['Inventory','Management','Real-time'],
      gradient: 'from-blue-500 to-indigo-500'
    },
    {
      category: 'data-analytics',
      title: 'N8N Workflow Directory',
      description: 'Automation workflows and templates for n8n platform',
      metrics: 'Workflow automation',
      tags: ['n8n','Automation','Workflows'],
      gradient: 'from-green-500 to-teal-500'
    },
    {
      category: 'data-analytics',
      title: 'Creator Insights',
      description: 'Advanced analytics and insights for content creators',
      metrics: 'Creator metrics',
      tags: ['Analytics','Insights','Content'],
      gradient: 'from-orange-500 to-red-500'
    },
    // Tools,Utilities & Miscellaneous
    {
      category: 'tools',
      title: 'LLMBridgeClaudeCode',
      description: 'Bridge for using local LLMs with Anthropic-style clients',
      metrics: '70% cost reduction',
      tags: ['LLM','Local','Bridge'],
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      category: 'tools',
      title: 'Prompta Toolkit',
      description: 'Advanced prompt engineering and management toolkit',
      metrics: 'Prompt engineering',
      tags: ['Prompts','Engineering','Toolkit'],
      gradient: 'from-cyan-500 to-blue-500'
    },
    {
      category: 'tools',
      title: 'AI Prompts 365',
      description: 'Daily AI prompt inspiration and template collection',
      metrics: 'Daily prompts',
      tags: ['Prompts','Daily','Collection'],
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      category: 'tools',
      title: 'BMAD Method',
      description: 'Development methodology and framework toolkit',
      metrics: 'Methodology framework',
      tags: ['Methodology','Framework','Development'],
      gradient: 'from-indigo-500 to-purple-500'
    },
    {
      category: 'tools',
      title: 'ProjectFlow Manager',
      description: 'Streamlined project workflow and task management',
      metrics: 'Project management',
      tags: ['Project','Management','Workflow'],
      gradient: 'from-teal-500 to-green-500'
    },
    {
      category: 'tools',
      title: 'Telos Deep Context Framework',
      description: 'Advanced context management for AI applications',
      metrics: 'Context framework',
      tags: ['Context','Framework','AI'],
      gradient: 'from-blue-500 to-indigo-500'
    }
  ];

  const filteredProjects=selectedCategory==='all' ? vibecodingMVPs : vibecodingMVPs.filter(project=> project.category===selectedCategory);

  return (
    <div className="relative">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-purple/10 rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Header */}
      <section ref={headerRef} className="py-24 lg:py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 20}}
            animate={headerInView ? {opacity: 1,y: 0} : {}}
            transition={{duration: 0.6}}
            className="text-center"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary-500/20 to-accent-purple/20 border border-primary-500/30 text-sm text-primary-300 mb-8">
              <SafeIcon icon={FiStar} className="w-4 h-4 mr-2" />
              Platform-Agnostic AI Development Solutions
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8">
              <span className="bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
                Solutions Showcase
              </span>
            </h1>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Comprehensive VibeCoding solutions from rapid MVP development to enterprise implementations. See how we transform ideas into production-ready applications across any platform or technology stack.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tabbed Navigation */}
      <section ref={tabsRef} className="py-12 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 20}}
            animate={tabsInView ? {opacity: 1,y: 0} : {}}
            transition={{duration: 0.6}}
          >
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-16">
              {tabs.map((tab)=> (
                <motion.button
                  key={tab.id}
                  whileHover={{scale: 1.05}}
                  whileTap={{scale: 0.95}}
                  onClick={()=> setActiveTab(tab.id)}
                  className={`flex items-center px-6 py-4 rounded-2xl text-sm font-medium transition-all duration-300 ${
                    activeTab===tab.id
                      ? 'bg-gradient-to-r from-primary-500 to-accent-purple text-white shadow-lg shadow-primary-500/25'
                      : 'bg-dark-800/60 text-gray-300 hover:text-white hover:bg-dark-700/60 border border-dark-600/30'
                  }`}
                >
                  <SafeIcon icon={tab.icon} className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <div className="font-semibold">{tab.name}</div>
                    <div className="text-xs opacity-80">{tab.description}</div>
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-12 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* AI Studio Projects Tab - Now First */}
          {activeTab==='ai-studio' && (
            <motion.div
              initial={{opacity: 0,y: 20}}
              animate={{opacity: 1,y: 0}}
              transition={{duration: 0.6}}
            >
              <div className="text-center mb-16">
                <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-white">
                  Google AI Studio to Production MVP
                </h2>
                <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
                  From idea to interactive MVP in days—these projects prove our speed,quality,and scope. We validate with Gemini-powered workflows,harden with guardrails,and ship production-ready pilots.
                </p>

                {/* Category Filter */}
                <div className="flex flex-wrap justify-center gap-3">
                  {portfolioCategories.map((category)=> (
                    <motion.button
                      key={category.id}
                      whileHover={{scale: 1.05}}
                      whileTap={{scale: 0.95}}
                      onClick={()=> setSelectedCategory(category.id)}
                      className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                        selectedCategory===category.id
                          ? 'bg-primary-500/20 text-primary-300 border border-primary-500/30'
                          : 'bg-dark-800/60 text-gray-300 hover:text-white hover:bg-dark-700/60 border border-dark-600/30'
                      }`}
                    >
                      <SafeIcon icon={category.icon} className="w-4 h-4 mr-2" />
                      {category.name}
                      <span className="ml-2 text-xs bg-dark-600/50 px-2 py-1 rounded-full">
                        {category.count}
                      </span>
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Projects Grid */}
              <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.map((project,index)=> (
                  <motion.div
                    key={`${project.title}-${index}`}
                    layout
                    initial={{opacity: 0,scale: 0.9}}
                    animate={{opacity: 1,scale: 1}}
                    exit={{opacity: 0,scale: 0.9}}
                    transition={{duration: 0.3,delay: index * 0.05}}
                    whileHover={{y: -5,scale: 1.02}}
                    className="group bg-dark-800/40 backdrop-blur-sm rounded-2xl p-6 border border-dark-600/30 hover:border-primary-500/30 transition-all duration-300"
                  >
                    <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r ${project.gradient} rounded-xl mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <SafeIcon icon={FiCode} className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-lg font-bold text-white mb-3 group-hover:text-primary-300 transition-colors">
                      {project.title}
                    </h4>
                    <p className="text-gray-400 text-sm leading-relaxed mb-4">
                      {project.description}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1 mb-4">
                      {project.tags.map((tag,tagIndex)=> (
                        <span
                          key={tagIndex}
                          className="text-xs bg-dark-700/50 text-gray-300 px-2 py-1 rounded border border-dark-600/30"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="text-xs text-primary-400 font-medium bg-primary-500/10 px-2 py-1 rounded mb-4 border border-primary-500/20">
                      {project.metrics}
                    </div>

                    {/* Removed GitHub and Live links */}
                    <div className="text-center">
                      <span className="text-xs text-gray-500">
                        Internal Project
                      </span>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* Portfolio Summary */}
              <motion.div
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{duration: 0.6,delay: 0.4}}
                className="mt-16 text-center"
              >
                <div className="bg-dark-800/40 backdrop-blur-sm rounded-3xl p-8 border border-dark-600/30">
                  <h4 className="text-2xl font-bold text-white mb-4">
                    {vibecodingMVPs.length}+ Google AI Studio Validated MVPs
                  </h4>
                  <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                    Every solution starts in Google AI Studio for rapid validation,then transforms into production-ready MVPs. From concept to deployment in days,not months.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary-400 mb-1">{vibecodingMVPs.length}+</div>
                      <div className="text-sm text-gray-400">Studio Projects</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-400 mb-1">95%</div>
                      <div className="text-sm text-gray-400">Success Rate</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-400 mb-1">3-7</div>
                      <div className="text-sm text-gray-400">Days to MVP</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-400 mb-1">100%</div>
                      <div className="text-sm text-gray-400">Gemini Powered</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Use Cases Tab */}
          {activeTab==='use-cases' && (
            <motion.div
              initial={{opacity: 0,y: 20}}
              animate={{opacity: 1,y: 0}}
              transition={{duration: 0.6}}
            >
              <div className="text-center mb-16">
                <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-white">
                  Real-World Use Cases
                </h2>
                <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                  VibeCoding applications across different business functions transforming operations,support,sales,and development workflows.
                </p>
              </div>

              <div className="space-y-16">
                {useCases.map((useCase,index)=> (
                  <motion.div
                    key={index}
                    initial={{opacity: 0,y: 20}}
                    animate={{opacity: 1,y: 0}}
                    transition={{duration: 0.6,delay: index * 0.1}}
                    className="bg-dark-800/30 backdrop-blur-sm rounded-3xl p-8 lg:p-12 border border-dark-700/30"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                      <div>
                        <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${useCase.gradient} rounded-2xl mb-6 shadow-lg`}>
                          <SafeIcon icon={useCase.icon} className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                          {useCase.title}
                        </h3>
                        <div className="space-y-6">
                          <div>
                            <h4 className="text-lg font-semibold text-white mb-2 flex items-center">
                              <SafeIcon icon={FiTarget} className="w-5 h-5 text-red-400 mr-2" />
                              Problem
                            </h4>
                            <p className="text-gray-300">{useCase.problem}</p>
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-white mb-2 flex items-center">
                              <SafeIcon icon={FiArrowRight} className="w-5 h-5 text-blue-400 mr-2" />
                              Solution
                            </h4>
                            <p className="text-gray-300">{useCase.solution}</p>
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-white mb-2 flex items-center">
                              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-green-400 mr-2" />
                              Outcome
                            </h4>
                            <p className="text-gray-300">{useCase.outcome}</p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="bg-dark-700/50 backdrop-blur-sm rounded-2xl p-6 border border-dark-600/30 mb-6">
                          <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                            <SafeIcon icon={FiBarChart3} className="w-5 h-5 text-primary-400 mr-2" />
                            Key Metrics
                          </h4>
                          <div className="space-y-3">
                            {useCase.metrics.map((metric,metricIndex)=> (
                              <div key={metricIndex} className="flex items-center">
                                <div className="w-2 h-2 bg-primary-400 rounded-full mr-3"></div>
                                <span className="text-gray-300">{metric}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="bg-gradient-to-r from-primary-500/10 to-accent-purple/10 backdrop-blur-sm rounded-2xl p-6 border border-primary-500/20">
                          <h5 className="font-semibold text-primary-300 mb-2">Build Basis</h5>
                          <p className="text-primary-200 text-sm">{useCase.buildBasis}</p>
                          <div className="flex items-center text-primary-200 mt-4">
                            <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
                            <span className="text-sm">2-6 weeks from concept to deployment</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Case Studies Tab */}
          {activeTab==='case-studies' && (
            <motion.div
              initial={{opacity: 0,y: 20}}
              animate={{opacity: 1,y: 0}}
              transition={{duration: 0.6}}
            >
              <div className="text-center mb-16">
                <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-white">
                  Featured Case Studies
                </h2>
                <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                  In-depth analysis of our most impactful VibeCoding implementations with proven results and measurable outcomes.
                </p>
              </div>

              <div className="space-y-20">
                {featuredCaseStudies.map((study,index)=> (
                  <motion.div
                    key={index}
                    initial={{opacity: 0,y: 20}}
                    animate={{opacity: 1,y: 0}}
                    transition={{duration: 0.6,delay: index * 0.2}}
                    className="bg-dark-800/40 backdrop-blur-sm rounded-3xl overflow-hidden border border-dark-600/30"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                      {/* Main Content */}
                      <div className="lg:col-span-2 p-8 lg:p-12">
                        <div className="flex items-center mb-6">
                          <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${study.gradient} rounded-2xl mr-4 shadow-lg`}>
                            <SafeIcon icon={study.icon} className="w-8 h-8 text-white" />
                          </div>
                          <div>
                            <h3 className="text-3xl lg:text-4xl font-bold text-white">{study.title}</h3>
                            <p className="text-primary-400 font-medium">{study.subtitle}</p>
                          </div>
                        </div>
                        <div className="space-y-8">
                          {/* Context */}
                          <div>
                            <h4 className="text-lg font-semibold text-white mb-3">Context</h4>
                            <p className="text-gray-300">{study.context}</p>
                          </div>
                          {/* Approach */}
                          <div>
                            <h4 className="text-lg font-semibold text-white mb-3">Approach</h4>
                            <ul className="space-y-2">
                              {study.approach.map((item,itemIndex)=> (
                                <li key={itemIndex} className="flex items-start">
                                  <div className="w-2 h-2 bg-primary-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                                  <span className="text-gray-300">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          {/* Results */}
                          <div>
                            <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-green-400 mr-2" />
                              Results
                            </h4>
                            <ul className="space-y-2">
                              {study.results.map((result,resultIndex)=> (
                                <li key={resultIndex} className="flex items-start">
                                  <div className="w-2 h-2 bg-green-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                                  <span className="text-gray-300">{result}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          {/* Artifacts - Removed repository link */}
                          <div>
                            <h4 className="text-lg font-semibold text-white mb-3">Artifacts</h4>
                            <div className="flex items-center">
                              <SafeIcon icon={FiCode} className="w-5 h-5 text-gray-400 mr-2" />
                              <span className="text-gray-300">{study.artifacts}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Metrics Sidebar */}
                      <div className="bg-dark-700/50 backdrop-blur-sm p-8 border-l border-dark-600/30">
                        <h4 className="text-lg font-semibold text-white mb-6">Key Metrics</h4>
                        <div className="space-y-6">
                          {Object.entries(study.metrics).map(([key,value],metricIndex)=> (
                            <div key={metricIndex} className="text-center">
                              <div className="text-2xl font-bold text-primary-400 mb-1">{value}</div>
                              <div className="text-sm text-gray-400 capitalize">
                                {key.replace(/([A-Z])/g,' $1').trim()}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-8 p-4 bg-gradient-to-r from-primary-500/10 to-accent-purple/10 rounded-2xl border border-primary-500/20">
                          <h5 className="font-semibold text-primary-300 mb-2">Implementation</h5>
                          <div className="space-y-2 text-sm text-primary-200">
                            <div className="flex items-center">
                              <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
                              <span>2-4 weeks development</span>
                            </div>
                            <div className="flex items-center">
                              <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
                              <span>1-2 developers</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-accent-purple/10 to-accent-cyan/10 blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-dark-800/40 backdrop-blur-xl rounded-4xl p-12 border border-dark-600/30">
            <h3 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Ready to Transform Your Vision into Reality?
              </span>
            </h3>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Let's discuss how VibeCoding can accelerate your next project. From rapid prototypes to enterprise solutions,we deliver results in days,not months.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.div whileHover={{scale: 1.05}} whileTap={{scale: 0.95}}>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-500 to-accent-purple text-white rounded-2xl font-semibold hover:shadow-2xl hover:shadow-primary-500/25 transition-all duration-300"
                >
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2" />
                  Start Your Project
                </Link>
              </motion.div>
              <motion.div whileHover={{scale: 1.05}} whileTap={{scale: 0.95}}>
                <Link
                  to="/demos"
                  className="inline-flex items-center px-8 py-4 bg-dark-800/60 backdrop-blur-sm border border-dark-600/50 text-white rounded-2xl font-semibold hover:bg-dark-700/60 hover:border-primary-500/50 transition-all duration-300"
                >
                  <SafeIcon icon={FiPlay} className="w-5 h-5 mr-2" />
                  Watch Demos
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;